import { useId, type ButtonHTMLAttributes, type ReactNode, type Ref } from 'react';

import { cn } from '@/lib/cn';

import { ICONS } from './icons';
import { Tooltip, TooltipProvider } from './Tooltip';

/**
 * ================================================================================================
 * FIRE NIVÅER, OG BARE ETT AV DEM HAR FARGET FYLL (Glass 2, DESIGN.md §16.5 punkt 1, G6).
 *
 *   primary    aksentfyll med lysrefleksen (`--primary-gradient`, `--shadow-primary`). Maks én per
 *              skjerm: øverst til høyre på siden, eller nederst i en dialog.
 *   secondary  TONAL: grått fyll, ingen kant. Standardknappen.
 *   tertiary   tekst i aksentfarge, vekt 600, ofte med «+» foran («+ Legg ved fil», «+ Ny lenke»).
 *   ghost      bare ikon, eller ikon med dempet tekst — verktøyknapper: piler, «Kolonner», «…».
 *
 * `danger` (fylt rød) finnes bare i bekreftelsesdialogen, der knappen ER hele valget.
 *
 * HVA SOM ER IGJEN AV A1. UX-ordren A1 krevde kant på hver knapp, fordi den vanligste handlingen
 * (`ghost` med dempet tekst) målte nøyaktig samme grå som metadataen ved siden av. G6 opphever
 * kanten — «kant betyr inndatafelt» — men funnet står: EN HANDLING SKAL ALDRI SE UT SOM METADATA.
 * Derfor har tonal et fyll, tertiær har aksentfarge og vekt 600, og ghost med dempet tekst er
 * bare lov med et ikon foran (tolkning 6: ikonet er det som gjør den til en handling).
 *
 * `quiet` OG `quietDanger` ER ALIAS, ikke egne nivåer. De er brukt i flere hundre kallsteder, og
 * det de betydde — «den svakeste knappen som fortsatt er en knapp» — er nøyaktig det tonal er nå.
 * `quiet` tegnes derfor som `secondary`, og `quietDanger` som `secondary` med rød tekst: formen
 * er tonal, faren ligger i fargen, og en rad med «Slett» roper fortsatt ikke. Nye kallsteder
 * skriver `secondary`/`tertiary`/`ghost`; aliasene er for de gamle.
 * ================================================================================================
 */
export type ButtonVariant =
  | 'primary'
  | 'secondary'
  | 'tertiary'
  | 'ghost'
  | 'danger'
  /** Alias for `secondary` (Glass 2). */
  | 'quiet'
  /** Alias for `secondary` med rød tekst (Glass 2). */
  | 'quietDanger';
export type ButtonSize = 'sm' | 'md' | 'lg';

/** Tonal: `control-tonal` er fyllet (og hoverfyllet), og følger `controls-on-backdrop`. */
const TONAL = 'control-tonal text-default active:translate-y-px';

const VARIANT: Record<ButtonVariant, string> = {
  primary: cn(
    'text-on-accent font-semibold',
    '[background:var(--primary-gradient)] shadow-[var(--shadow-primary)]',
    'hover:brightness-[.96] active:translate-y-px active:brightness-[.92]',
  ),
  secondary: TONAL,
  quiet: TONAL,
  quietDanger: cn('control-tonal text-danger active:translate-y-px'),
  tertiary: cn(
    'bg-transparent text-accent-text font-semibold',
    'hover:bg-accent-subtle active:translate-y-px',
  ),
  ghost: cn(
    'bg-transparent text-muted',
    'hover:bg-[var(--fill-tonal)] hover:text-default active:translate-y-px',
  ),
  danger: 'bg-danger text-on-accent font-semibold hover:brightness-[.94] active:translate-y-px',
};

/**
 * AVSLÅTT ER GRÅTT, UANSETT NIVÅ (QA-runde 13, punkt 2).
 *
 * Avslått ble tegnet som nivået sitt på 50 % — og en tertiær knapp på 50 % er fortsatt blå tekst,
 * som leses som en lenke man kan trykke på («Fjern» på den eneste plattformadminen). Nå mister en
 * knapp som ikke kan brukes både fargen og fyllet sitt: tonal flate (eller ingen, der nivået ikke
 * har noen) og svak tekst. `loading` er IKKE avslått i denne betydningen — den beholder nivået og
 * spinneren, fordi handlingen er i gang og ikke nektet.
 */
const UNAVAILABLE: Record<ButtonVariant, string> = {
  primary: 'bg-[var(--control-fill,var(--fill-tonal))] text-faint font-semibold',
  secondary: 'bg-[var(--control-fill,var(--fill-tonal))] text-faint',
  quiet: 'bg-[var(--control-fill,var(--fill-tonal))] text-faint',
  quietDanger: 'bg-[var(--control-fill,var(--fill-tonal))] text-faint',
  danger: 'bg-[var(--control-fill,var(--fill-tonal))] text-faint font-semibold',
  tertiary: 'bg-transparent text-faint font-semibold',
  ghost: 'bg-transparent text-faint',
};

/**
 * 28 px i tabeller og verktøylinjer, 36 px i skjema (UX-ordren A1), 40 px for den ene knappen som
 * står alene under et skjema. Høydene er uendret i Glass 2; bare polstringen er litt bredere,
 * fordi en kapsel mister hjørnene der teksten ellers ville hatt luft.
 */
const SIZE: Record<ButtonSize, string> = {
  sm: 'h-7 px-3 text-xs gap-1.5',
  md: 'h-9 px-3.5 text-sm gap-2',
  lg: 'h-10 px-4 text-sm gap-2',
};

/** Tertiær og ghost har ingen flate å fylle ut, så teksten står nærmere kanten. */
const TIGHT: Partial<Record<ButtonVariant, Record<ButtonSize, string>>> = {
  tertiary: { sm: 'px-2', md: 'px-2.5', lg: 'px-3' },
  ghost: { sm: 'px-2', md: 'px-2.5', lg: 'px-3' },
};

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
  block?: boolean;
  /**
   * HVORFOR KNAPPEN IKKE KAN BRUKES — og dermed AT den ikke kan (QA13 runde 2, J4).
   *
   * En streng betyr «kan ikke brukes, og dette er hvorfor»; `null` betyr «kan brukes».
   *
   * `aria-disabled`, IKKE `disabled` (QA-runde 13, punkt 2). En native `disabled` tar knappen ut av
   * tab-rekkefølgen og (med `pointer-events: none`) bort fra pekeren — så forklaringen kunne aldri
   * vises for noen. Nå er knappen pekbar og fokuserbar, grunnen står i en tooltip ved mus OG fokus,
   * og et klikk (mus, Enter, mellomrom, eller et skjemas Enter i et felt) gjør ingenting.
   * `loading` og en eksplisitt `disabled` setter fortsatt den native og trenger ingen grunn.
   */
  disabledReason?: string | null;
  ref?: Ref<HTMLButtonElement>;
  children?: ReactNode;
}

export function Button({
  variant = 'secondary',
  size = 'md',
  loading = false,
  block = false,
  className,
  disabled,
  disabledReason,
  title,
  children,
  type = 'button',
  onClick,
  'aria-describedby': describedBy,
  ...rest
}: ButtonProps): ReactNode {
  const Spinner = ICONS.spinner;
  const reasonId = useId();
  const native = disabled === true || loading;
  /* Grunnen gjelder bare når knappen ellers kunne vært trykket; en native avslåing vinner. */
  const reason = native ? null : (disabledReason ?? null);
  const ariaDisabled =
    reason !== null || rest['aria-disabled'] === true || rest['aria-disabled'] === 'true';
  /* En `title` kalleren selv satte vinner: den er mer spesifikk enn den generiske grunnen. */
  const explanation = reason === null ? null : (title ?? reason);
  /* Har kalleren alt pekt på en synlig grunn ved knappen, leser skjermleseren den og ikke en kopi. */
  const describeReason = explanation !== null && describedBy === undefined;

  const button = (
    <button
      type={type}
      disabled={native}
      title={explanation === null ? title : undefined}
      aria-busy={loading || undefined}
      aria-describedby={describeReason ? reasonId : describedBy}
      data-variant={variant}
      onClick={(event) => {
        /* Også skjemaets Enter i et felt: implisitt innsending er et klikk på standardknappen. */
        if (ariaDisabled) {
          event.preventDefault();
          return;
        }
        onClick?.(event);
      }}
      className={cn(
        // `hit-target` er 44 px-gulvet (DESIGN.md §9), i basen og ikke per kallsted: en golv som
        // må huskes er ikke et gulv. Den tegnede knappen endrer ikke størrelse — `::after` vokser.
        'hit-target',
        // Kapsel (G4). Kanten er gjennomsiktig og står bare for at fokusregelen i `globals.css`
        // (kantfarge + innfelt skygge) har en kant å farge — knappen tegner ingen (G6).
        'inline-flex items-center justify-center rounded-[var(--radius-control)] border border-transparent',
        'font-medium whitespace-nowrap',
        'transition-[background-color,color,filter] duration-[var(--duration-fast)] ease-[var(--ease-out)]',
        'ring-focus disabled:pointer-events-none',
        loading && 'disabled:opacity-50',
        // aria-disabled holder kontrollen fokuserbar og pekbar, så en tooltip kan forklare hvorfor.
        'aria-disabled:cursor-not-allowed',
        ariaDisabled || (native && !loading) ? UNAVAILABLE[variant] : VARIANT[variant],
        SIZE[size],
        TIGHT[variant]?.[size],
        block && 'w-full',
        className,
      )}
      {...rest}
      aria-disabled={ariaDisabled || undefined}
    >
      {loading ? <Spinner aria-hidden className="size-3.5 animate-spin" /> : null}
      {children}
    </button>
  );

  /*
   * SAMME TRE MED OG UTEN GRUNN. En knapp som tar `disabledReason` (også `null`) står alltid i en
   * tooltip, som bare holdes lukket når det ikke er noen grunn. Byttet den tre når grunnen kom og
   * gikk, monterte React knappen på nytt — fokuset falt av, og en referanse til den ble død.
   * Knapper uten propen får ingen tooltip-maskineri i det hele tatt.
   *
   * Egen leverandør: appens `TooltipProvider` finnes ikke i alle trær en knapp tegnes i (tester,
   * feilgrenser), og en Radix-tooltip uten leverandør kaster.
   */
  if (disabledReason === undefined) return button;
  return (
    <>
      <TooltipProvider delayDuration={200}>
        <Tooltip content={explanation} disabled={explanation === null}>
          {button}
        </Tooltip>
      </TooltipProvider>
      {/*
        Beskrivelsen for skjermleseren, også når tooltipen er lukket. VED SIDEN AV knappen og ikke
        inni den: inni ble den en del av knappens tekst (`textContent`), og «I kalenderen» ble
        «I kalenderenDelingen ligger alt …» for alt som leser teksten. `hidden` tar den ut av
        oppsettet (ingen flex-celle, ingen `gap`); `aria-describedby` leser skjult tekst likevel.
      */}
      {describeReason ? (
        <span hidden id={reasonId}>
          {explanation}
        </span>
      ) : null}
    </>
  );
}
